import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import type { Product, StockMovement } from '@bazaar/shared';
import { api, ApiError } from '../../lib/api.js';
import { Spinner } from '../../components/Spinner.js';
import { EmptyState } from '../../components/EmptyState.js';
import { BigButton } from '../../components/BigButton.js';
import { Page, PageHeader, Field } from '../../components/layout.js';

const PAGE_SIZE = 50;

const REASON_LABELS: Record<StockMovement['reason'], string> = {
  sale: 'Sale',
  void: 'Void',
  restock: 'Restock',
  adjustment: 'Adjustment',
};

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('en-MY', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  }).format(new Date(iso));
}

export function StockMovementsPage() {
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [products, setProducts] = useState<Product[]>([]);
  const [productId, setProductId] = useState<number | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .listProducts({ includeInactive: true })
      .then((r) => setProducts(r.products))
      .catch(() => {});
  }, []);

  const load = useCallback(async (pid: number | '', fromDate: string, toDate: string, off: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listStockMovements({
        productId: pid === '' ? undefined : pid,
        from: fromDate || undefined,
        to: toDate || undefined,
        limit: PAGE_SIZE,
        offset: off,
      });
      setMovements(res.movements);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load stock movements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(productId, from, to, offset);
  }, [productId, from, to, offset, load]);

  function productLabel(id: number): { code: string; name: string } {
    const p = products.find((p) => p.id === id);
    return p ? { code: p.code, name: p.name } : { code: `#${id}`, name: '' };
  }

  /**
   * The stock written when a product is first created is logged with
   * reason 'adjustment' (still the right bucket for reporting) but its
   * note is 'Opening stock' - show that instead of a bare "Adjustment"
   * pill, so a new stall's log doesn't open with a wall of movements that
   * look like corrections. See the QA report, P3 #22.
   */
  function reasonLabel(m: StockMovement): string {
    if (m.reason === 'adjustment' && m.note === 'Opening stock') return 'Opening stock';
    return REASON_LABELS[m.reason];
  }

  return (
    <Page>
      <PageHeader
        title="Stock movements"
        actions={
          <a
            href={`/api/export/stock-movements.csv${productId !== '' ? `?productId=${productId}` : ''}${
              from && to ? `${productId !== '' ? '&' : '?'}from=${from}&to=${to}` : ''
            }`}
          >
            <BigButton variant="outline">Export CSV</BigButton>
          </a>
        }
      />

      <div className="toolbar">
        <Field label="Product" htmlFor="stock-product-filter">
          <select
            id="stock-product-filter"
            value={productId}
            onChange={(e) => {
              setOffset(0);
              setProductId(e.target.value === '' ? '' : Number(e.target.value));
            }}
            // Bounded + truncated: a product's code/name can be long enough
            // (see QA-LONGCODE) that an unbounded <select> grows to fit its
            // widest <option> and forces the whole page to scroll sideways
            // at every desktop/tablet width - see the QA report, P2 #7.
            className="input sm:w-72 min-w-0 max-w-full truncate"
          >
            <option value="">All products</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} &middot; {p.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3 w-full sm:w-auto sm:flex sm:gap-3">
          <Field label="From" htmlFor="stock-from">
            <input
              id="stock-from"
              type="date"
              value={from}
              onChange={(e) => {
                setOffset(0);
                setFrom(e.target.value);
              }}
              className="input"
            />
          </Field>
          <Field label="To" htmlFor="stock-to">
            <input
              id="stock-to"
              type="date"
              value={to}
              onChange={(e) => {
                setOffset(0);
                setTo(e.target.value);
              }}
              className="input"
            />
          </Field>
        </div>
      </div>

      {loading && movements.length === 0 ? (
        <Spinner label="Loading stock movements" />
      ) : error ? (
        <EmptyState title="Could not load stock movements" description={error} />
      ) : movements.length === 0 ? (
        <EmptyState title="No stock movements" description="Restocks, adjustments, sales and voids show here." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] text-left border-collapse">
            <thead>
              <tr className="font-body font-semibold text-sm text-muted border-b border-border">
                <th className="px-3 py-3 first:pl-0 last:pr-0">Time</th>
                <th className="px-3 py-3 first:pl-0 last:pr-0">Product</th>
                <th className="px-3 py-3 first:pl-0 last:pr-0">Reason</th>
                <th className="px-3 py-3 first:pl-0 last:pr-0 text-right">Change</th>
                <th className="px-3 py-3 first:pl-0 last:pr-0">By</th>
                <th className="px-3 py-3 first:pl-0 last:pr-0 min-w-[10rem]">Note</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((m) => {
                const p = productLabel(m.productId);
                return (
                  <tr key={m.id} className="border-b border-border font-body text-base text-text">
                    <td className="px-3 py-3 first:pl-0 last:pr-0 whitespace-nowrap">{formatDateTime(m.createdAt)}</td>
                    <td className="px-3 py-3 first:pl-0 last:pr-0">
                      <span className="font-semibold">{p.code}</span>
                      {p.name && <span className="text-muted"> &middot; {p.name}</span>}
                    </td>
                    <td className="px-3 py-3 first:pl-0 last:pr-0">
                      <span className="rounded-full bg-aqua text-teal-dark font-bold text-sm px-2 py-0.5">
                        {reasonLabel(m)}
                      </span>
                    </td>
                    <td
                      className={clsx(
                        'px-3 py-3 first:pl-0 last:pr-0 text-right tabular-nums font-heading font-semibold',
                        m.delta >= 0 ? 'text-teal-dark' : 'text-danger',
                      )}
                    >
                      {m.delta >= 0 ? `+${m.delta}` : m.delta}
                    </td>
                    <td className="px-3 py-3 first:pl-0 last:pr-0">
                      {m.byUserName} <span className="font-semibold">({m.byUserCode})</span>
                    </td>
                    <td className="px-3 py-3 first:pl-0 last:pr-0 min-w-[10rem] text-muted">
                      {m.note ?? ''}
                      {m.refSaleId !== null && (
                        <Link
                          to={`/admin/sales?saleId=${m.refSaleId}`}
                          className="ml-2 inline-flex items-center min-h-12 min-w-12 px-2 rounded text-teal-dark underline"
                        >
                          Sale #{m.refSaleId}
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex items-center justify-between pt-3">
            <p className="font-body text-sm text-muted">
              {offset + 1}&ndash;{Math.min(offset + PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex gap-2">
              <BigButton variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                Prev
              </BigButton>
              <BigButton
                variant="outline"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </BigButton>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
